import React, { useCallback, useEffect, useState } from "react";
import { X, ArrowUpRight, CalendarPlus } from "lucide-react";
import TechKroky from "./TechKroky";
import { Spinner } from "./ui/kit";
import { FazePill, etapa } from "../lib/faze";
import { fmtKc } from "../lib/format";
import { KrokCip } from "./ObchodTabule";
import api, { apiError } from "../lib/api";

/**
 * Náhled případu z tabule nebo seznamu (⋯ → Náhled, mezerník): technologie
 * s tlačítky dalšího kroku — stejné jako v detailu případu (brány hlídá server).
 */
export default function PripadNahled({ polozka, money, isOwner, smiPlanovat, onClose, onDetail, onNaplanovat, onKrok, onZmena }) {
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [odjizdi, setOdjizdi] = useState(false);
  const load = useCallback(async () => {
    try { setData((await api.get(`/api/obchodni-pripady/${polozka.id}`)).data.pripad); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); }
  }, [polozka.id]);
  useEffect(() => { setData(null); load(); }, [load]);
  const zavrit = useCallback(() => { if (odjizdi) return; setOdjizdi(true); setTimeout(onClose, 220); }, [odjizdi, onClose]);
  useEffect(() => {
    // Esc zavře náhled, jen když nad ním není otevřený dialog kroku
    const esc = (e) => { if (e.key === "Escape" && document.querySelectorAll('[aria-modal="true"]').length <= 1) zavrit(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [zavrit]);
  const poZmene = () => { load(); onZmena?.(); };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-stretch justify-center md:justify-end md:p-3" data-testid="pripad-nahled">
      <div className={`absolute inset-0 bg-black/20 ${odjizdi ? "zaclon-out" : "zaclon-in"}`} onClick={zavrit} />
      <div className={`karta-panel ${odjizdi ? "odjizdi" : ""} relative bg-white w-full md:w-[440px] rounded-t-sheet md:rounded-[20px] shadow-[0_18px_50px_rgba(20,22,26,.22)] max-h-[92vh] md:max-h-none md:h-full overflow-y-auto`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} role="dialog" aria-modal="true" aria-label={polozka.nazev}>
        <div className="sticky top-0 bg-white z-10 px-5 pt-4 pb-3 border-b border-line-softer">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="t-sekce font-800 text-ink">{polozka.nazev}</h2>
              <div className="t-meta text-muted truncate">{polozka.client_name} · {polozka.cislo}</div>
            </div>
            <button type="button" onClick={zavrit} aria-label="Zavřít" className="tap -mr-2 -mt-1 text-muted hover:text-ink"><X size={20} /></button>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <FazePill faze={etapa(polozka)} />
            {money && <span className="amount t-telo font-800 text-ink ml-auto">{fmtKc(polozka.hodnota || 0)} Kč</span>}
          </div>
        </div>

        <div className="px-5 py-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <span className="label-up text-muted">Další krok</span>
            {polozka.dalsi_krok || !["DOKONCENO", "ZTRACENO"].includes(polozka.faze)
              ? <KrokCip p={polozka} onOtevri={onKrok} />
              : <span className="t-meta text-muted">Případ je uzavřený.</span>}
            {smiPlanovat && (
              <button type="button" onClick={() => onNaplanovat(polozka)} data-testid="nahled-naplanovat"
                className="tap md:min-h-0 self-start inline-flex items-center gap-1.5 h-9 t-meta font-700 text-sec hover:text-ink">
                <CalendarPlus size={15} /> Naplánovat další krok
              </button>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <span className="label-up text-muted">Technologie</span>
            {chyba && <div className="t-meta text-danger">{chyba}</div>}
            {!data && !chyba && <div className="py-6 flex justify-center"><Spinner /></div>}
            {data && (data.technologie || []).map((t) => (
              <div key={t.id} className="rounded-[12px] border border-line px-3.5 py-3 flex flex-col gap-2" data-testid={`nahled-tech-${t.id}`}>
                <div className="flex items-center gap-2">
                  <span className="t-telo font-700 text-ink flex-1 min-w-0 truncate">{t.kategorie_name}</span>
                  <FazePill faze={etapa(t)} />
                </div>
                {(t.akce || []).some((a) => a.smi)
                  ? <div className="flex flex-wrap items-center gap-1.5"><TechKroky compact tech={t} pripad={data} isOwner={isOwner} onChanged={poZmene} /></div>
                  : <span className="t-meta text-muted">{t.faze === "DOKONCENO" ? "Předáno, zařízení je v servisu." : t.faze === "ZTRACENO" ? "Ztracená." : "Další krok udělá jiná role."}</span>}
              </div>
            ))}
          </div>

          <button type="button" onClick={() => onDetail(polozka)} data-testid="nahled-detail"
            className="btn-ghost tap md:min-h-0 h-10 justify-center inline-flex items-center gap-1.5">
            Otevřít detail <ArrowUpRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
